import React, { useState, useEffect } from 'react'
import DropDownSelect from '../../components/GenericDropDownSelect'
import SubHeader from '../../components/WithSubHeaderBG'
import { Box } from '../../designSystem'
import { TabsHolder, Tab, TabNameSpan, NovelHeading, ErrorText } from './style'
import Overview from './Overview'
import { get } from '../../lib/api'
import Engagement from './Engagement'
import Loader from '../../components/Loader'
import Revenue from './Revenue'
import { handlePageLoadEventLog } from '../../lib/utils'
import GiftRevenue from './GiftRevenue'

const AnalyticsUI = () => {
  const [selectedTab, setSelectedTab] = useState(0)
  const [bookList, setBookList] = useState([])
  const [error, updateError] = useState('')
  const [isLoading, updateIsLoading] = useState(true)
  const [bookSelected, updateBookSelected] = useState(null)
  const [bookStats, updateBookStats] = useState(null)
  const [revenueStats, updateRevenueStats] = useState(null)
  const [giftRevenueStats, updateGiftRevenueStats] = useState(null)
  const [tabs, updateTabs] = useState(['Overview', 'Engagement'])
  const [entryInCMS, updateEntryInCMS] = useState(false)
  const [isSeries, setIsSeries] = useState(false)

  const fetchBooks = () => {
    const params = new URLSearchParams(window.location.search)
    get('/content_api/book.cbp_book_list?is_novel=1').then((response) => {
      setBookList(response)
      if (params.has('bookId')) {
        onChangeBook(params.get('bookId'))
        setSelectedTab(1)
      } else {
        updateBookSelected(response[0]?.book_id)
        setIsSeries(response[0]?.is_novel === 2)
      }
    })
  }

  const onChangeBook = (bookID) => {
    let selectedBook = bookList.filter((bk) => bk.book_id == bookID)[0] || {}
    setIsSeries(selectedBook?.is_novel === 2)
    updateBookSelected(bookID)
  }

  const fetchRevenueAnalysis = () => {
    if (!bookSelected) return
    get(`/user_api/novel_revenue_share?book_id=${bookSelected}&is_novel=1`)
      .then((response) => {
        updateRevenueStats(response)
        updateEntryInCMS(true)
        updateTabs(['Overview', 'Engagement', 'Revenue'])
        fetchGiftRevenueAnalysis()
        updateIsLoading(false)
      })
      .catch(() => {
        updateEntryInCMS(false)
        updateIsLoading(false)
      })
  }

  const fetchGiftRevenueAnalysis = () => {
    if (!bookSelected) return
    get(`/user_api/novel_gift_revenue_share?book_id=${bookSelected}`)
      .then((response) => {
        updateGiftRevenueStats(response)
        // updateEntryInCMS(true)
        updateTabs(['Overview', 'Engagement', 'Revenue', 'Gift Revenue'])
        updateIsLoading(false)
      })
      .catch(() => {
        // updateEntryInCMS(false)
        updateIsLoading(false)
      })
  }
  const fetchBookAnalysis = () => {
    if (!bookSelected) return
    get(`/user_api/novel_analytics?book_id=${bookSelected}&is_novel=1`)
      .then((response) => {
        updateBookStats(response)
        fetchRevenueAnalysis()
      })
      .catch(() => {
        updateBookStats(null)
        updateIsLoading(false)
        updateError('Data not found, please try again later')
      })
  }

  const fetchSeriesAnalysis = () => {
    if (!bookSelected) return
    get(`/user_api/novel_analytics_ugc?book_id=${bookSelected}&is_novel=2`)
      .then((response) => {
        updateIsLoading(false)
        updateBookStats(response)
      })
      .catch(() => {
        updateBookStats(null)
        updateIsLoading(false)
        updateError('Data will be generated shortly post the series publishes 10 or more chapters.')
      })
  }

  useEffect(() => {
    fetchBooks()
  }, [])

  useEffect(() => {
    updateTabs(['Overview', 'Engagement'])
    updateEntryInCMS(false)
    updateIsLoading(true)
    updateError('')
    updateBookStats(null)
    if(!isSeries){
      fetchBookAnalysis()
    }else{
      fetchSeriesAnalysis()
    }
  }, [bookSelected])

  useEffect(() => {
    if (selectedTab === 0)
      handlePageLoadEventLog('analytics_overview', {
        show_id: bookSelected ?? '',
      })
    else if (selectedTab === 1)
      handlePageLoadEventLog('analytics_engagement', {
        show_id: bookSelected ?? '',
      })
    else if (selectedTab === 2)
      handlePageLoadEventLog('analytics_revenue', {
        show_id: bookSelected ?? '',
      })
  }, [selectedTab, bookSelected])

  const isMobile = window.screen.width < 600

  return (
    <SubHeader title="Analytics">
      <Box
        variant="flex"
        backgroundColor="surface.foreground"
        p={16}
        alignItems="center"
      >
        {isMobile && <NovelHeading>{isSeries ? 'Series' : 'Novels'}</NovelHeading>}
        <DropDownSelect
          name="book"
          placeholder="Name of the novel"
          selectOptions={bookList}
          idKey="book_id"
          nameKey="book_title"
          value={bookSelected}
          clickOnSelect
          books
          customWidth="60vw"
          onChange={(bookID) => onChangeBook(bookID)}
        />
      </Box>
      {isLoading && <Loader />}
      <Box my="8px">
        {error.length > 0 && <ErrorText>{error}</ErrorText>}
        {!isLoading && error.length < 1 && (
          <TabsHolder>
            {tabs.map((tab, i) => (
              <Tab
                key={tab}
                last={i === tabs.length - 1}
                active={selectedTab === i}
                onClick={() => setSelectedTab(i)}
              >
                <TabNameSpan
                  active={selectedTab === i}
                  alwaysActive={selectedTab === i}
                >
                  {tab}
                </TabNameSpan>
              </Tab>
            ))}
          </TabsHolder>
        )}
        {bookStats && !isLoading && selectedTab === 0 && (
          !isSeries ? <Overview
            showRevenue={entryInCMS}
            dailyRevenue={revenueStats?.daily_net_revenue || []}
            dailyRevenueShare={revenueStats?.daily_revenue_share || []}
            dailyActiveReads={bookStats?.daily_active_readers}
            bookSelected={bookSelected}
          /> :
          <Overview
            showRevenue={entryInCMS}
            // dailyRevenue={revenueStats?.daily_active_listeners || []}
            // dailyRevenueShare={revenueStats?.daily_revenue_share || []}
            dailyActiveReads={bookStats?.daily_active_listeners}
            bookSelected={bookSelected}
            isSeries={isSeries}
          />
        )}
        {bookStats && !isLoading && selectedTab === 1 && ( !isSeries ?
          <Engagement
            bookValidReaders={bookStats?.valid_readers}
            bookReads={bookStats?.chapter_reads}
            platformAverageBookReaders={bookStats?.benchmark_readers}
            bookSelected={bookSelected}
          /> : 
          <Engagement
            bookValidReaders={bookStats?.valid_listeners}
            bookSelected={bookSelected}
            isSeries={isSeries}
          /> 
        )}
        {!isLoading && revenueStats && entryInCMS && selectedTab === 2 && (
          <Revenue
            monthlyRevenue={revenueStats?.monthly_revenue || []}
            chapterRevenue={revenueStats?.chapter_revenue || []}
            monthlyRevenueShare={revenueStats?.monthly_revenue_share || []}
            chapterRevenueShare={revenueStats?.chapter_revenue_share || []}
            payingUser={revenueStats?.paying_users || 0}
            netRevenue={revenueStats?.total_net_revenue || 0}
            revenueShare={revenueStats?.total_net_revenue_share || 0}
            incentive={revenueStats?.total_incentives || 0}
            bookSelected={bookSelected}
          />
        )}
        {!isLoading && giftRevenueStats && selectedTab === 3 && (
          <GiftRevenue
            giftRevenueStats={giftRevenueStats}
            bookSelected={bookSelected}
          />
        )}
      </Box>
    </SubHeader>
  )
}

export default AnalyticsUI
